import type { TourConfig } from './types';

export const dietPlansTour: TourConfig = {
  id: 'diet-plans-tab',
  name: 'Diet Plans Tour',
  description: 'Learn how to create and manage weekly diet plans',
  requiresClientContext: true,
  steps: [
    {
      element: '[data-tour="diet-plans-content"]',
      popover: {
        title: 'Diet Plans',
        description:
          "Create and manage weekly diet plans for your client. Let's explore the key features.",
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="diet-week-selector"]',
      popover: {
        title: 'Select Week',
        description:
          'Click here to navigate to different weeks. The diet plan is organized as a 7-day view.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="diet-import-template"]',
      popover: {
        title: 'Import from Template',
        description:
          'Quickly populate the week by importing from your saved diet templates.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="diet-save-template"]',
      popover: {
        title: 'Save as Template',
        description:
          'Save the current week as a reusable diet template that you can quickly apply to other clients.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="diet-copy-diet"]',
      popover: {
        title: 'Copy Diet',
        description:
          "Copy this week's diet to your clipboard. Navigate to another client or week and paste it to duplicate the plan.",
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="diet-share"]',
      popover: {
        title: 'Share & Export',
        description:
          'Share diet plans via WhatsApp or export as a PDF. Choose custom date ranges to include multiple weeks in one document.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="diet-save-buttons"]',
      popover: {
        title: 'Save & Publish',
        description:
          'Save your changes as a draft, or publish to make the plan visible to the client.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      element: '[data-tour="diet-grid"]',
      popover: {
        title: '7-Day Meal Grid',
        description:
          'Each column is a day, each row is a meal type. The grid shows nutrition totals per day and a weekly average summary.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: '[data-tour="diet-day-status"]',
      popover: {
        title: 'Day Status',
        description:
          'Each day shows its status: Published (visible to client), Draft (saved but not shared), or Unsaved (pending changes).',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="diet-day-nutrition"]',
      popover: {
        title: 'Daily Nutrition Totals',
        description:
          'View the total calories, protein, carbs, and fat for each day. Hover for detailed breakdown.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="diet-meal-name"]',
      popover: {
        title: 'Customize Meal Names',
        description:
          'Click on a meal name to edit it, or use the dropdown to select from common options like Breakfast, Lunch, etc.',
        side: 'right',
        align: 'center',
      },
    },
    {
      element: '[data-tour="diet-meal-time"]',
      popover: {
        title: 'Set Meal Times',
        description:
          'Set the time for each meal type. This applies across all days for that meal.',
        side: 'right',
        align: 'center',
      },
    },
    {
      element: '[data-tour="diet-add-food"]',
      popover: {
        title: 'Adding & Editing Foods',
        description:
          'Click the pencil icon to open the meal editor. Search for foods, add custom items, and set quantities. Foods with recipes show a book icon - click to view. Incompatible foods (e.g., non-veg for vegetarian client) display a warning.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: '[data-tour="diet-copy-actions"]',
      popover: {
        title: 'Copy & Paste',
        description:
          'Copy individual cells, entire meal rows, or full days to quickly duplicate meals. Use the copy icons that appear.',
        side: 'left',
        align: 'center',
      },
    },
    {
      element: '[data-tour="diet-add-meal"]',
      popover: {
        title: 'Custom Meals',
        description:
          'Add additional meal rows beyond the defaults if your client needs more meals per day.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: '[data-tour="diet-focus-mode"]',
      popover: {
        title: 'Focus Mode',
        description:
          'Toggle focus mode to expand the diet grid to full screen, hiding the sidebar and header for distraction-free editing.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="diet-action-bar"]',
      popover: {
        title: 'Quick Actions Bar',
        description:
          'This floating bar appears when you have unsaved changes. Use undo/redo to revert edits, save as draft, or publish directly.',
        side: 'top',
        align: 'center',
      },
    },
  ],
};
